import React from 'react';
import { Modal, notification } from 'antd';
import type { AddressProps } from '@partsbadger/library';
import ContactStore from 'stores/ContactStore';
import SalesOrderStore from 'stores/SalesOrderStore';
import AddressForm from '../AddressForm';
import { ErrorRender } from '@partsbadger/library';

interface Props {
  open: boolean;
  type: 'Billing' | 'Shipping' | 'Billing and Shipping';
  onClose: () => void;
  initial?: AddressProps | null;
  onSaved?: (addr: AddressProps) => void;
}

const AddAddressModal: React.FC<Props> = ({ open, type, onClose, initial, onSaved }) => {
  const isEdit = !!initial?.id;

  const applyToOrder = async (addr: AddressProps) => {
    const prefix = type === 'Billing' ? 'billing' : 'shipping';
    const countryCode = typeof addr.country === 'string' ? addr.country : addr.country?.code || '';

    const payload = {
      [`${prefix}_name`]: addr.name || '',
      [`${prefix}_street`]: addr.street || '',
      [`${prefix}_city`]: addr.city || '',
      [`${prefix}_state`]: addr.state || '',
      [`${prefix}_zip_code`]: addr.zip_code || '',
      [`${prefix}_country`]: countryCode || '',
    };

    const orderId = SalesOrderStore?.order?.id;

    if (!orderId) return;

    try {
        const before = SalesOrderStore?.order;
      await SalesOrderStore.patchOrderDraft(orderId, payload);
        const after = SalesOrderStore?.order;
    } catch (err: any) {
      notification.error({
        duration: 10,
        message: 'Error',
        description: <ErrorRender error={err} />,
        placement: 'bottomRight',
      });
    }
  };

  const handleSave = async (value: AddressProps) => {

  const countryRaw = (value as any)?.country;
  const countryCode =
    typeof countryRaw === 'string' ? countryRaw : (countryRaw?.code ?? countryRaw?.iso2 ?? countryRaw?.abbr ?? null);


    const contactId = ContactStore.contact?.id;
    if (!contactId) {
      notification.error({ message: 'No contact loaded.' });
      return Promise.reject();
    }

    const payload: AddressProps = {
      ...value,
      type,
      primary: value.primary ?? initial?.primary ?? false,
    };

    try {
      const saved = isEdit
        ? await ContactStore.patchAddress(contactId, initial!.id, payload)
        : await ContactStore.saveAddress(contactId, payload);

      await ContactStore.getContactById(contactId);
      await applyToOrder(saved);

      onSaved?.(saved);

      notification.success({ message: `${type} address ${isEdit ? 'updated' : 'saved'}` });
      onClose();
      return saved;
    } catch (e) {
      notification.error({ message: `Could not ${isEdit ? 'update' : 'save'} address.` });
      return Promise.reject(e);
    }
  };

  return (
    <Modal
      title={`${isEdit ? 'Edit' : 'Add'} ${type} Address`}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      maskClosable={false}
    >
      <AddressForm
        addressType={type}
        initial={initial ?? undefined}
        handleSave={handleSave}
        handleClose={onClose}
      />
    </Modal>
  );
};

export default AddAddressModal;
